class ComparisonTable extends HTMLElement {
  constructor() {
    super();
    this.init();
    this.lastScrollTop = 0; // Track last scroll position
    this.setupScrollBehavior();

    this.header = this.querySelector("[data-comparison-table-header]");
  }

  init() {
    this.setupAccordion();
    this.setupTooltips();
    this.setupShowMore();
    this.setupMobileRowScroller();
    this.setupHorizontalSync();
    this.setupDesktopColumnLayout();
  }

  setupDesktopColumnLayout() {
    const mq = window.matchMedia("(min-width: 990px)");

    const clearLayout = () => {
      this.querySelectorAll(
        ".comparison-table__body .comparison-table__column--info, .comparison-table__body .comparison-table__products-wrapper"
      ).forEach((cell) => {
        cell.style.width = "";
        cell.style.minWidth = "";
        cell.style.maxWidth = "";
        cell.style.flex = "";
      });
    };

    const applyLayout = () => {
      clearLayout();
      if (!mq.matches) return;

      this.querySelectorAll(".comparison-table__body .comparison-table__row").forEach((row) => {
        const info = row.querySelector(":scope > .comparison-table__column--info");
        const scroller = row.querySelector(":scope > .comparison-table__row-scroller");
        const products = scroller
          ? Array.from(scroller.querySelectorAll(".comparison-table__products-wrapper"))
          : Array.from(row.querySelectorAll(":scope > .comparison-table__products-wrapper"));

        if (!info || !products.length) return;

        const rowWidth = row.getBoundingClientRect().width;
        const infoWidth = info.getBoundingClientRect().width;
        const colWidth = Math.floor((rowWidth - infoWidth) / products.length);

        if (colWidth <= 0) return;

        info.style.boxSizing = "border-box";

        products.forEach((cell) => {
          cell.style.boxSizing = "border-box";
          cell.style.width = `${colWidth}px`;
          cell.style.minWidth = `${colWidth}px`;
          cell.style.maxWidth = `${colWidth}px`;
          cell.style.flex = "0 0 auto";
        });
      });
    };

    this._applyDesktopColumnLayout = applyLayout;
    applyLayout();

    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", applyLayout);
    } else {
      mq.addListener(applyLayout);
    }

    window.addEventListener("resize", applyLayout);
  }

  setupScrollBehavior() {
    if (!this.header) return;

    const mobileMediaQuery = window.matchMedia("(max-width: 989px)");

    const updateHeaderPosition = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const isMobile = mobileMediaQuery.matches;

      // Determine scroll direction and apply appropriate position
      if (scrollTop > this.lastScrollTop) {
        // Scrolling DOWN
        this.header.style.top = isMobile ? "-100px" : "0";
      } else {
        // Scrolling UP
        this.header.style.top = isMobile ? "-34px" : "70px";
      }

      this.lastScrollTop = scrollTop;
    };

    // Listen for scroll events
    window.addEventListener("scroll", updateHeaderPosition);

    // Listen for resize events to handle switching between mobile and desktop
    mobileMediaQuery.addListener(updateHeaderPosition);
  }

  setupAccordion() {
    const headers = this.querySelectorAll("[data-group-header='true']");
    headers.forEach((button) => {
      button.addEventListener("click", () => {
        const isExpanded = button.getAttribute("aria-expanded") === "true";
        button.setAttribute("aria-expanded", !isExpanded);
        const content = button.nextElementSibling;
        content.setAttribute("aria-hidden", isExpanded);
        button.closest(".comparison-table__group").classList.toggle("comparison-table__group--closed", isExpanded);

        // Toggle visibility
        if (isExpanded) {
          content.style.maxHeight = null; // Collapse
          content.style.opacity = "0"; // Fade out
          content.style.visibility = "hidden"; // Hide
        } else {
          content.style.maxHeight = content.scrollHeight + "px"; // Expand
          content.style.opacity = "1"; // Fade in
          content.style.visibility = "visible"; // Show
        }

        if (typeof this._applyDesktopColumnLayout === "function") {
          requestAnimationFrame(() => this._applyDesktopColumnLayout());
        }
      });
    });
  }

  setupShowMore() {
    const showMoreButton = this.querySelector("[data-show-more='true']");
    const showLessButton = this.querySelector("[data-show-less='true']");
    const body = this.querySelector(".comparison-table__body");

    const relayout = () => {
      if (typeof this._applyDesktopColumnLayout === "function") {
        requestAnimationFrame(() => this._applyDesktopColumnLayout());
      }
    };

    if (showMoreButton) {
      showMoreButton.addEventListener("click", () => {
        body.classList.add("comparison-table__body--expanded");
        showMoreButton.style.display = "none";
        if (showLessButton) showLessButton.style.display = "block";
        relayout();
      });
    }

    if (showLessButton) {
      showLessButton.addEventListener("click", () => {
        body.classList.remove("comparison-table__body--expanded");
        showLessButton.style.display = "none";
        if (showMoreButton) showMoreButton.style.display = "block";
        relayout();
      });
    }
  }

  setupTooltips() {
    const tooltipTriggers = this.querySelectorAll(".comparison-table__tooltip-trigger");
    tooltipTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        const tooltip = trigger.nextElementSibling;
        const isVisible = tooltip.getAttribute("aria-hidden") === "false";

        // Hide all tooltips
        this.querySelectorAll(".comparison-table__custom-tooltip").forEach((t) => {
          t.setAttribute("aria-hidden", "true");
          t.style.opacity = "0";
          t.style.visibility = "hidden";
        });

        // Toggle the clicked tooltip
        tooltip.setAttribute("aria-hidden", isVisible);
        tooltip.style.opacity = isVisible ? "0" : "1";
        tooltip.style.visibility = isVisible ? "hidden" : "visible";
      });
    });

    // Close tooltip when clicking outside
    document.addEventListener("click", (event) => {
      if (!event.target.matches(".comparison-table__tooltip-trigger")) {
        this.querySelectorAll(".comparison-table__custom-tooltip").forEach((t) => {
          t.setAttribute("aria-hidden", "true");
          t.style.opacity = "0";
          t.style.visibility = "hidden";
        });
      }
    });

    document.querySelectorAll(".comparison-table__tooltip-trigger").forEach((trigger) => {
      trigger.addEventListener("mouseenter", handleTooltipPosition);
      trigger.addEventListener("focus", handleTooltipPosition);
    });

    function handleTooltipPosition(event) {
      const tooltip = event.target.nextElementSibling;
      if (!tooltip) return;

      // Reset classes
      tooltip.classList.remove("comparison-table__custom-tooltip--right", "comparison-table__custom-tooltip--left");

      // Get tooltip dimensions and position
      const tooltipRect = tooltip.getBoundingClientRect();
      const triggerRect = event.target.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Check if tooltip would overflow right side
      if (triggerRect.left + tooltipRect.width / 2 > viewportWidth - 20) {
        tooltip.classList.add("comparison-table__custom-tooltip--right");
      }
      // Check if tooltip would overflow left side
      else if (triggerRect.left - tooltipRect.width / 2 < 20) {
        tooltip.classList.add("comparison-table__custom-tooltip--left");
      }
    }

    // Add resize handler to reposition tooltips if needed
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const visibleTooltip = document.querySelector(
          '.comparison-table__custom-tooltip[style*="visibility: visible"]'
        );
        if (visibleTooltip) {
          const trigger = visibleTooltip.previousElementSibling;
          if (trigger) {
            handleTooltipPosition({ target: trigger });
          }
        }
      }, 100);
    });
  }

  setupMobileRowScroller() {
    const mq = window.matchMedia("(max-width: 989px)");

    const wrapRows = () => {
      const rows = this.querySelectorAll(".comparison-table__row");
      rows.forEach((row) => {
        // Skip if already wrapped
        if (row.querySelector(":scope > .comparison-table__row-scroller")) return;

        const info = row.querySelector(":scope > .comparison-table__column--info");
        if (!info) return;

        const scroller = document.createElement("div");
        scroller.className = "comparison-table__row-scroller";

        const productCells = Array.from(row.querySelectorAll(":scope > .comparison-table__products-wrapper"));
        if (!productCells.length) return;

        // Insert scroller after info column
        if (info.nextSibling) row.insertBefore(scroller, info.nextSibling);
        else row.appendChild(scroller);

        // Move product cells into scroller
        for (const cell of productCells) scroller.appendChild(cell);
      });
    };

    const unwrapRows = () => {
      const scrollers = this.querySelectorAll(".comparison-table__row-scroller");
      scrollers.forEach((scroller) => {
        const row = scroller.parentElement;
        const cells = Array.from(scroller.children);
        for (const cell of cells) row.appendChild(cell);
        scroller.remove();
      });
    };

    const handleChange = () => {
      if (mq.matches) {
        wrapRows();
      } else {
        unwrapRows();
      }
      // Rebind scroll sync if available
      if (typeof this._rebindScrollSync === "function") this._rebindScrollSync();
      if (typeof this._applyDesktopColumnLayout === "function") {
        this._applyDesktopColumnLayout();
      }
    };

    // Initial
    handleChange();

    // Listen for viewport changes
    if (typeof mq.addEventListener === "function") mq.addEventListener("change", handleChange);
    else mq.addListener(handleChange);
  }

  setupHorizontalSync() {
    const getScrollers = () => {
      const header = this.querySelector("[data-comparison-table-header]");
      const rowScrollers = this.querySelectorAll(".comparison-table__row-scroller");
      const rows = rowScrollers.length ? rowScrollers : this.querySelectorAll(".comparison-table__row");
      return [header, ...rows].filter(Boolean);
    };

    let currentScrollers = getScrollers();
    if (!currentScrollers.length) return;

    let isSyncing = false;
    const onScroll = (e) => {
      if (isSyncing) return;
      const src = e.target;
      const srcMax = src.scrollWidth - src.clientWidth;
      const ratio = srcMax > 0 ? src.scrollLeft / srcMax : 0;
      isSyncing = true;
      for (const el of currentScrollers) {
        if (el === src) continue;
        const max = el.scrollWidth - el.clientWidth;
        const targetLeft = max > 0 ? Math.round(ratio * max) : 0;
        if (el.scrollLeft !== targetLeft) {
          el.scrollLeft = targetLeft;
        }
      }
      requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    const register = () => {
      currentScrollers = getScrollers();
      for (const el of currentScrollers) {
        el.removeEventListener("scroll", onScroll);
        if (el.scrollWidth > el.clientWidth) {
          el.addEventListener("scroll", onScroll, { passive: true });
        }
      }
    };

    register();
    window.addEventListener("resize", register);
    this._rebindScrollSync = register;
  }
}

if (!customElements.get("comparison-table")) {
  customElements.define("comparison-table", ComparisonTable);
}
